// Web sitesi (Faz 11): demo / iletişim formu (lead), "Servis operasyonunuz ne kadar verimli?" analiz aracı ve
// fiyatlandırma kataloğu. Girdiler Zod; analiz hesabı saf fonksiyon (site istemcide, API kayıtta aynı sonucu üretir).
import { z } from 'zod';
import type { PlanKey } from '../enums';

// ───────── lead (POST /v1/public/leads) ─────────

export const LEAD_SEGMENTS = ['SERVICE_COMPANY', 'SCHOOL', 'CORPORATE'] as const;
export type LeadSegment = (typeof LEAD_SEGMENTS)[number];
export const LEAD_SEGMENT_LABELS: Record<LeadSegment, string> = {
  SERVICE_COMPANY: 'Servis firması',
  SCHOOL: 'Okul',
  CORPORATE: 'Şirket',
};

export const LEAD_SOURCES = ['demo', 'analysis', 'contact'] as const;
export type LeadSource = (typeof LEAD_SOURCES)[number];

/** Aydınlatma metni sürümü: onay kaydı hangi metne verildiğini gösterir. Metin değişince artırılır. */
export const LEAD_KVKK_VERSION = '2026-10';

/** Hız sınırı: IP başına pencere içinde en fazla bu kadar başvuru. */
export const LEAD_RATE_LIMIT = { perIp: 5, windowS: 600 } as const;

// ───────── analiz aracı ─────────

export const ANALYSIS_CITIES = ['İstanbul', 'Ankara', 'İzmir', 'Bursa', 'Kocaeli', 'Antalya', 'Diğer'] as const;
export type AnalysisCity = (typeof ANALYSIS_CITIES)[number];

/** Form alanları metin olarak gelebilir (progressive enhancement); boş metin "girilmedi" sayılır. */
const blankToUndefined = (v: unknown) => (typeof v === 'string' && v.trim() === '' ? undefined : v);
const count = (label: string, max: number) =>
  z.preprocess(
    blankToUndefined,
    z.coerce
      .number({ message: `${label} için bir sayı yazın.` })
      .int(`${label} tam sayı olmalı.`)
      .min(1, `${label} en az 1 olmalı.`)
      .max(max, `${label} en fazla ${max.toLocaleString('tr-TR')} olabilir.`),
  );

export const AnalysisInput = z.object({
  vehicles: count('Araç sayısı', 2000),
  passengers: count('Yolcu sayısı', 100000),
  routes: count('Rota sayısı', 2000),
  city: z.enum(ANALYSIS_CITIES, { message: 'Şehir seçin.' }),
});
export type AnalysisInput = z.infer<typeof AnalysisInput>;

export type EfficiencyLevel = 'GOOD' | 'FAIR' | 'LOW';
export const EFFICIENCY_LEVEL_LABELS: Record<EfficiencyLevel, string> = {
  GOOD: 'Verimli',
  FAIR: 'Geliştirilebilir',
  LOW: 'Yüksek tasarruf potansiyeli',
};

export interface EfficiencyEstimate {
  /** 0–100 verimlilik puanı */
  score: number;
  level: EfficiencyLevel;
  /** varsayılan araç kapasitesi (koltuk) */
  seats: number;
  /** ortalama doluluk, 0–1 */
  occupancy: number;
  /** hedef doluluk, 0–1 */
  targetOccupancy: number;
  /** aynı yolcuyu taşımak için önerilen araç sayısı */
  suggestedVehicles: number;
  vehicleReduction: number;
  /** rota sadeleştirme ile km tasarrufu, 0–1 */
  kmSavingPct: number;
  monthlyKm: number;
  monthlyKmSaved: number;
  /** aylık tahmini tasarruf aralığı, kuruş */
  monthlySavingMin: number;
  monthlySavingMax: number;
  /** yolcu başına günlük yolculuk süresinden kazanç, dakika */
  minutesSavedPerPassenger: number;
  /** aylık CO₂ azaltımı, kg */
  co2KgSaved: number;
}

/**
 * Hesap varsayımları (site "Nasıl hesaplıyoruz?" bölümünde aynen gösterilir). Gerçek analiz rota ve durak verisiyle
 * yapılır; buradaki sonuç yalnız örnek tahmindir.
 */
export const ANALYSIS_ASSUMPTIONS = {
  targetOccupancy: 0.85,
  /** rota başına günlük km (sabah + akşam seferi) */
  dailyKmPerRoute: 64,
  workDaysPerMonth: 22,
  /** km başına değişken maliyet (yakıt, lastik, bakım), kuruş */
  costPerKm: 1450,
  /** araç başına aylık sabit maliyet (şoför, amortisman, sigorta), kuruş */
  vehicleMonthlyCost: 9000000,
  /** dizel minibüs, kg CO₂ / km */
  co2PerKm: 0.27,
  /** rota birleştirmeyle önerilebilecek en fazla araç azaltımı */
  maxVehicleReduction: 0.25,
  /** ortalama tek yön yolculuk süresi, dakika */
  avgRideMinutes: 50,
} as const;

/** Şehir trafik katsayısı: yoğun trafikte rota sadeleştirmenin km ve süre etkisi büyür. */
export const ANALYSIS_CITY_TRAFFIC: Record<AnalysisCity, number> = {
  İstanbul: 1.3,
  Ankara: 1.15,
  İzmir: 1.15,
  Bursa: 1.1,
  Kocaeli: 1.1,
  Antalya: 1.05,
  Diğer: 1,
};

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const round2 = (v: number) => Math.round(v * 100) / 100;

/** Yolcu/araç oranını taşıyan en küçük araç tipi: minibüs 16, midibüs 27, otobüs 46 koltuk. */
export function seatsFor(passengersPerVehicle: number): number {
  if (passengersPerVehicle <= 16) return 16;
  if (passengersPerVehicle <= 27) return 27;
  return 46;
}

/**
 * Deterministik verimlilik tahmini. Aynı girdi her zaman aynı sonucu verir; ağ veya rastgelelik yok.
 * Doluluk hedefin (%85) altındaysa araç azaltımı, trafik ve boş koltuk oranı arttıkça km tasarrufu önerilir.
 */
export function estimateEfficiency(input: AnalysisInput): EfficiencyEstimate {
  const A = ANALYSIS_ASSUMPTIONS;
  const vehicles = Math.max(1, Math.round(input.vehicles));
  const passengers = Math.max(1, Math.round(input.passengers));
  const routes = Math.max(1, Math.round(input.routes));
  const traffic = ANALYSIS_CITY_TRAFFIC[input.city] ?? 1;

  const seats = seatsFor(passengers / vehicles);
  const occupancy = clamp(passengers / (vehicles * seats), 0, 1);

  const byCapacity = Math.ceil(passengers / (seats * A.targetOccupancy));
  const floor = Math.ceil(vehicles * (1 - A.maxVehicleReduction));
  const suggestedVehicles = Math.min(vehicles, Math.max(1, byCapacity, floor));
  const vehicleReduction = vehicles - suggestedVehicles;

  // Araç başına birden fazla rota (vardiya) iyi kullanım; rotası araçtan azsa boş araç payı km'ye yansır.
  const idlePenalty = routes < vehicles ? clamp((vehicles - routes) / vehicles, 0, 1) * 0.04 : 0;
  const kmSavingPct = round2(clamp(0.06 + (1 - occupancy) * 0.12 + (traffic - 1) * 0.15 + idlePenalty, 0.04, 0.22));

  const monthlyKm = Math.round(routes * A.dailyKmPerRoute * A.workDaysPerMonth);
  const monthlyKmSaved = Math.round(monthlyKm * kmSavingPct);
  const saving = vehicleReduction * A.vehicleMonthlyCost + monthlyKmSaved * A.costPerKm;
  // ±%15 aralık, 1.000 TL'ye yuvarlanmış (kuruş)
  const toThousand = (k: number) => Math.round(k / 100000) * 100000;
  const monthlySavingMin = toThousand(saving * 0.85);
  const monthlySavingMax = toThousand(saving * 1.15);

  const minutesSavedPerPassenger = Math.round(A.avgRideMinutes * 2 * kmSavingPct);
  const co2KgSaved = Math.round(monthlyKmSaved * A.co2PerKm);

  const occScore = clamp(occupancy / A.targetOccupancy, 0, 1);
  const routeScore = 1 - (kmSavingPct - 0.04) / 0.18;
  const score = Math.round(clamp(100 * (0.65 * occScore + 0.35 * routeScore), 0, 100));
  const level: EfficiencyLevel = score >= 80 ? 'GOOD' : score >= 60 ? 'FAIR' : 'LOW';

  return {
    score,
    level,
    seats,
    occupancy: round2(occupancy),
    targetOccupancy: A.targetOccupancy,
    suggestedVehicles,
    vehicleReduction,
    kmSavingPct,
    monthlyKm,
    monthlyKmSaved,
    monthlySavingMin,
    monthlySavingMax,
    minutesSavedPerPassenger,
    co2KgSaved,
  };
}

// ───────── lead girdisi ─────────

const optionalText = (max: number, msg: string) =>
  z.preprocess(blankToUndefined, z.string().trim().max(max, msg).optional());

export const LeadInput = z
  .object({
    name: z
      .string({ message: 'Adınızı ve soyadınızı yazın.' })
      .trim()
      .min(2, 'Adınızı ve soyadınızı yazın.')
      .max(80, 'Ad soyad en fazla 80 karakter olabilir.'),
    company: z
      .string({ message: 'Firma veya kurum adını yazın.' })
      .trim()
      .min(2, 'Firma veya kurum adını yazın.')
      .max(120, 'Firma adı en fazla 120 karakter olabilir.'),
    email: z
      .string({ message: 'E-posta adresinizi yazın.' })
      .trim()
      .toLowerCase()
      .min(1, 'E-posta adresinizi yazın.')
      .max(160, 'E-posta en fazla 160 karakter olabilir.')
      .pipe(z.email('Geçerli bir e-posta adresi yazın.')),
    phone: z.preprocess(
      blankToUndefined,
      z
        .string()
        .trim()
        .max(20, 'Telefon en fazla 20 karakter olabilir.')
        .regex(/^[+0-9 ()-]{7,20}$/, 'Telefonu yalnız rakamla yazın, ör. 0532 000 00 00.')
        .optional(),
    ),
    segment: z.enum(LEAD_SEGMENTS, { message: 'Kurum tipini seçin.' }),
    vehicleCount: z.preprocess(
      blankToUndefined,
      z.coerce
        .number({ message: 'Araç sayısı için bir sayı yazın.' })
        .int('Araç sayısı tam sayı olmalı.')
        .min(0, 'Araç sayısı 0 veya daha büyük olmalı.')
        .max(5000, 'Araç sayısı en fazla 5.000 olabilir.')
        .optional(),
    ),
    message: optionalText(1000, 'Mesaj en fazla 1.000 karakter olabilir.'),
    source: z.enum(LEAD_SOURCES).default('demo'),
    /** yalnız source = analysis: analiz girdisi (sonuç sunucuda yeniden hesaplanır) */
    analysis: AnalysisInput.optional(),
    kvkkConsent: z.preprocess(
      (v) => v === true || v === 'true' || v === 'on' || v === '1',
      z.literal(true, { message: 'Devam etmek için aydınlatma metnini onaylayın.' }),
    ),
    /** bal küpü: insan doldurmaz; doluysa kayıt yapılmaz */
    website: z.string().max(200).optional(),
    /** JavaScript'siz gönderimde dönüş sayfası (yalnız site içi yol) */
    returnTo: z
      .string()
      .regex(/^\/[a-z0-9/-]*$/)
      .max(60)
      .optional(),
  })
  .superRefine((v, ctx) => {
    if (v.source === 'analysis' && !v.analysis) {
      ctx.addIssue({ code: 'custom', path: ['analysis'], message: 'Önce analiz bilgilerini doldurun.' });
    }
  });
export type LeadInput = z.infer<typeof LeadInput>;

export interface LeadResult {
  ok: true;
}

/** Lead.analysis JSON alanında saklanan yapı (onay kaydı + analiz). */
export interface LeadAnalysisRecord {
  consent: { kvkk: true; version: string; at: string };
  input?: AnalysisInput;
  result?: EfficiencyEstimate;
}

// ───────── fiyatlandırma kataloğu (site; seed planlarıyla aynı değerler) ─────────

export type WebsitePlanValue = boolean | string;

export interface WebsitePlan {
  key: PlanKey;
  name: string;
  /** aylık fiyat, kuruş, KDV hariç; null = teklif */
  monthlyPrice: number | null;
  tagline: string;
  vehicleLimit: number | null;
  passengerLimit: number | null;
  highlight?: boolean;
}

export const WEBSITE_PLANS: WebsitePlan[] = [
  {
    key: 'STARTER',
    name: 'Başlangıç',
    monthlyPrice: 490000,
    tagline: 'Küçük filolar için canlı takip ve yolcu bildirimleri.',
    vehicleLimit: 10,
    passengerLimit: 250,
  },
  {
    key: 'GROWTH',
    name: 'Büyüme',
    monthlyPrice: 1290000,
    tagline: 'Okul servisi, veli takibi ve NFC ile büyüyen firmalar için.',
    vehicleLimit: 40,
    passengerLimit: 1200,
    highlight: true,
  },
  {
    key: 'BUSINESS',
    name: 'Profesyonel',
    monthlyPrice: 2490000,
    tagline: 'Finans, hakediş ve rota optimizasyonuyla tüm operasyon.',
    vehicleLimit: 120,
    passengerLimit: 4000,
  },
  {
    key: 'ENTERPRISE',
    name: 'Kurumsal',
    monthlyPrice: null,
    tagline: 'Beyaz etiket, API ve sınırsız filo; size özel kurulum.',
    vehicleLimit: null,
    passengerLimit: null,
  },
];

export interface WebsitePlanRow {
  key: string;
  label: string;
  values: Record<PlanKey, WebsitePlanValue>;
}

const all = (v: WebsitePlanValue): Record<PlanKey, WebsitePlanValue> => ({
  STARTER: v,
  GROWTH: v,
  BUSINESS: v,
  ENTERPRISE: v,
});
const fromPlan = (key: PlanKey[]): Record<PlanKey, WebsitePlanValue> => ({
  STARTER: key.includes('STARTER'),
  GROWTH: key.includes('GROWTH'),
  BUSINESS: key.includes('BUSINESS'),
  ENTERPRISE: key.includes('ENTERPRISE'),
});

/** Karşılaştırma tablosu. Modül satırları seed planlarındaki `modules` ile aynıdır. */
export const WEBSITE_PLAN_ROWS: WebsitePlanRow[] = [
  {
    key: 'vehicles',
    label: 'Araç limiti',
    values: { STARTER: '10 araç', GROWTH: '40 araç', BUSINESS: '120 araç', ENTERPRISE: 'Sınırsız' },
  },
  {
    key: 'passengers',
    label: 'Yolcu',
    values: { STARTER: '250 yolcu', GROWTH: '1.200 yolcu', BUSINESS: '4.000 yolcu', ENTERPRISE: 'Sınırsız' },
  },
  { key: 'liveTracking', label: 'Canlı takip ve ETA', values: all(true) },
  { key: 'notifications', label: 'Yolcu bildirimleri', values: all(true) },
  {
    key: 'sms',
    label: 'Aylık SMS kotası',
    values: { STARTER: '1.000', GROWTH: '5.000', BUSINESS: '15.000', ENTERPRISE: 'Size özel' },
  },
  { key: 'qr', label: 'QR / PIN ile biniş', values: all(true) },
  {
    key: 'reports',
    label: 'Raporlar',
    values: { STARTER: 'Temel', GROWTH: 'Gelişmiş', BUSINESS: 'Gelişmiş', ENTERPRISE: 'Gelişmiş' },
  },
  { key: 'school', label: 'Okul ve veli modülü', values: fromPlan(['GROWTH', 'BUSINESS', 'ENTERPRISE']) },
  { key: 'nfc', label: 'NFC kart ile biniş', values: fromPlan(['GROWTH', 'BUSINESS', 'ENTERPRISE']) },
  { key: 'ai', label: 'AI rota optimizasyonu', values: fromPlan(['BUSINESS', 'ENTERPRISE']) },
  { key: 'finance', label: 'Finans ve hakediş', values: fromPlan(['BUSINESS', 'ENTERPRISE']) },
  { key: 'whiteLabel', label: 'Beyaz etiket', values: fromPlan(['ENTERPRISE']) },
  { key: 'api', label: 'API ve webhook', values: fromPlan(['ENTERPRISE']) },
];
